// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import { getSupabaseKey } from '@/lib/env';
import type { Database } from './types';

const SUPABASE_URL = "https://service.canxp.ai";
const SUPABASE_PUBLISHABLE_KEY = getSupabaseKey();
const ELECTRON_FIRST_BOOT_LOGIN_KEY = 'electron-first-boot-login-complete';

const isElectronRenderer = () => {
  return typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('electron');
};

const clearElectronFirstBootAuthCache = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined' || !isElectronRenderer()) {
    return;
  }

  const hasCompletedFirstBootLogin = localStorage.getItem(ELECTRON_FIRST_BOOT_LOGIN_KEY) === 'true';
  if (hasCompletedFirstBootLogin) {
    return;
  }

  for (const key of Object.keys(localStorage)) {
    if (key === 'supabase.auth.token' || (/^sb-.*-auth-token$/i).test(key)) {
      localStorage.removeItem(key);
    }
  }
};

clearElectronFirstBootAuthCache();

// Function to check if we're running in Lovable preview

const isLovablePreview = () => {
  return typeof window !== 'undefined' && 
         window.location.hostname.includes('lovableproject.com');
};

// Function to implement retry with exponential backoff
const fetchWithRetry = async (url: string, options: RequestInit, retries = 3, backoff = 300) => {
  try {
    return await fetch(url, options);
  } catch (err) {
    if (retries === 0) {
      throw err;
    }
    
    const isResourceError = err.toString().includes('ERR_INSUFFICIENT_RESOURCES');
    const waitTime = isResourceError ? backoff * 2 : backoff;
    
    console.log(`Retrying fetch (${retries} attempts left) after ${waitTime}ms...`);
    await new Promise(resolve => setTimeout(resolve, waitTime));
    
    return fetchWithRetry(url, options, retries - 1, backoff * 2);
  }
};

// Track ongoing requests to implement concurrency limiting
const activeRequests = {
  count: 0,
  max: 10,
  queue: [] as (() => void)[],
  
  async acquire() {
    if (this.count < this.max) {
      this.count++;
      return true;
    }
    
    // Queue this request
    return new Promise<boolean>(resolve => {
      this.queue.push(() => resolve(true));
    });
  },
  
  release() {
    this.count--;
    if (this.queue.length > 0 && this.count < this.max) {
      const next = this.queue.shift();
      if (next) next();
    }
  }
};

// Create Supabase client with improved configuration
export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
    global: {
      fetch: async (...args: Parameters<typeof fetch>) => {
        const isAttachmentRequest = args[0].toString().includes('/attachments');
        
        if (isAttachmentRequest) {
          await activeRequests.acquire();
        }
        
        try {
          const options = args[1] || {};
          
          const isAuthRequest = args[0].toString().includes('/auth/v1/token');
          const retries = isAuthRequest ? 5 : 3;
          const backoffTime = isAuthRequest ? 500 : 300;
          
          const response = await fetchWithRetry(args[0] as string, options as RequestInit, retries, backoffTime)
            .catch(err => {
              // Downgrade to warn to avoid spamming error logs for transient issues.
              const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
              if (online) {
                console.warn('Fetch issue in Supabase client:', err);
              } else {
                console.info('Fetch skipped/failed while offline in Supabase client.');
              }
              throw err;
            });
          
          return response;
        } finally {
          if (isAttachmentRequest) {
            activeRequests.release();
          }
        }
      },
      headers: {
        'X-Client-Info': 'ChatCanvas PWA',
      }
    }
  }
);

// Add utility function to check if we're online
export const isOnline = () => typeof navigator !== 'undefined' && navigator.onLine === true;

// Throttle auth refresh requests coming from the Service Worker.
let __authRefreshInFlight: Promise<any> | null = null;
let __lastAuthRefreshAt = 0;
const __MIN_AUTH_REFRESH_INTERVAL_MS = 30_000;

async function requestAuthRefreshThrottled() {
  const now = Date.now();
  if (__authRefreshInFlight) return __authRefreshInFlight;
  if (now - __lastAuthRefreshAt < __MIN_AUTH_REFRESH_INTERVAL_MS) return null;
  __lastAuthRefreshAt = now;

  __authRefreshInFlight = (async () => {
    try {
      return await refreshAuthToken();
    } finally {
      __authRefreshInFlight = null;
    }
  })();

  return __authRefreshInFlight;
}

export const refreshAuthToken = async () => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      console.log('No session to refresh');
      return false;
    }

    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      // Stop noisy loops on invalid/expired tokens: warn and sign out.
      console.warn('Auth token refresh failed; signing out to clear invalid session:', error);
      await supabase.auth.signOut();
      return false;
    }

    if (data?.session) {
      console.log('Auth token refreshed successfully');
      return { session: data.session, user: data.session.user };
    }

    return false;
  } catch (err) {
    console.warn('Exception during token refresh; signing out to recover:', err);
    await supabase.auth.signOut();
    return false;
  }
};

export const registerServiceWorker = async () => {
  const isElectron =
    typeof window !== 'undefined' &&
    typeof window.process === 'object' &&
    !!window.process.versions?.electron;

  if (isElectron || location.protocol === 'file:') {
    console.warn('[SW] Skipping service worker registration in Electron or file:// protocol');
    return null;
  }

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker-2.8.0.js', {
        scope: '/'
      });

      console.log('ServiceWorker registration successful with scope:', registration.scope);

      // Handle SW update flow - prevent infinite reload
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              // A new service worker has been installed.
              // We can notify the user that an update is available,
              // but we won't force a reload here to prevent loops.
              // The new service worker will activate on the next page load/refresh.
              console.log('New service worker installed and ready. It will activate on next page load.');
            }
          });
        }
      });

      // Handle custom message from SW (like auth refresh)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type !== 'AUTH_REFRESH_NEEDED') return;

        // Avoid doing heavy async work inside the message handler.
        // Schedule and throttle refresh to prevent postMessage storms.
        window.setTimeout(() => {
          void requestAuthRefreshThrottled();
        }, 0);
      });

      return registration;
    } catch (error) {
      console.error('ServiceWorker registration failed:', error);
      return null;
    }
  }

  return null;
};